import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../css/about.css';
import me2 from '../assets/me.jpeg';
import cvEn from '../assets/CV_EN.pdf';
import cvEs from '../assets/CV_ES.pdf';
import {
    BiLogoJavascript, BiLogoReact, BiLogoNodejs, BiLogoGithub, BiLogoTypescript,
    BiLogoVuejs, BiLogoFirebase, BiLogoHtml5, BiLogoCss3, BiLogoBootstrap
} from "react-icons/bi";
import { SiExpo, SiExpress, SiMysql, SiPostgresql, SiTailwindcss } from "react-icons/si";
import { ReactComponent as OdooIcon } from '../assets/icons/odoo.svg';
import { ReactComponent as RenderIcon } from '../assets/icons/render.svg';
import { FaAws, FaDatabase } from "react-icons/fa";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { useT } from '../context/LanguageContext';

// Must match the pdfjs-dist version bundled with react-pdf (the file in /public
// is copied from node_modules/react-pdf/node_modules/pdfjs-dist/build).
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function useInView(threshold = 0.15) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// Keys for category styling — labels come from translations
const CATEGORIES_BASE = [
    {
        key: 'frontend',
        color: '#61DAFB',
        accent: 'rgba(97,218,251,0.06)',
        border: 'rgba(97,218,251,0.15)',
        skills: [
            { icon: BiLogoJavascript, name: 'JavaScript',  color: '#F7DF1E' },
            { icon: BiLogoReact,      name: 'React',        color: '#61DAFB' },
            { icon: BiLogoTypescript, name: 'TypeScript',   color: '#3178C6' },
            { icon: BiLogoVuejs,      name: 'Vue.js',       color: '#42B883' },
            { icon: SiTailwindcss,    name: 'Tailwind',     color: '#38BDF8' },
            { icon: BiLogoHtml5,      name: 'HTML',         color: '#E34F26' },
            { icon: BiLogoCss3,       name: 'CSS',          color: '#1572B6' },
            { icon: BiLogoBootstrap,  name: 'Bootstrap',    color: '#7952B3' },
        ],
    },
    {
        key: 'backend',
        color: '#68A063',
        accent: 'rgba(104,160,99,0.06)',
        border: 'rgba(104,160,99,0.15)',
        skills: [
            { icon: BiLogoNodejs,  name: 'Node.js',    color: '#68A063' },
            { icon: SiExpress,     name: 'Express',    color: '#ffffff' },
            { icon: SiMysql,       name: 'MySQL',      color: '#4479A1' },
            { icon: SiPostgresql,  name: 'PostgreSQL', color: '#336791' },
            { icon: FaDatabase,    name: 'API REST',   color: '#aaaaaa' },
            { icon: FaAws,         name: 'AWS',        color: '#FF9900' },
        ],
    },
    {
        key: 'mobile',
        color: '#a78bfa',
        accent: 'rgba(167,139,250,0.06)',
        border: 'rgba(167,139,250,0.15)',
        skills: [
            { icon: BiLogoReact, name: 'React Native', color: '#61DAFB' },
            { icon: SiExpo,      name: 'Expo',         color: '#ffffff' },
        ],
    },
    {
        key: 'tools',
        color: '#f472b6',
        accent: 'rgba(244,114,182,0.06)',
        border: 'rgba(244,114,182,0.15)',
        skills: [
            { icon: BiLogoGithub,   name: 'GitHub',   color: '#ffffff' },
            { icon: BiLogoFirebase, name: 'Firebase', color: '#FFCA28' },
            { icon: RenderIcon,     name: 'Render',   color: '#ffffff', isSvg: true },
            { icon: OdooIcon,       name: 'Odoo',     color: '#714B67', isSvg: true },
        ],
    },
];

function SkillPill({ icon: Icon, name, color, delay = 0, inView, isSvg = false }) {
    const [hovered, setHovered] = useState(false);
    const iconColor = hovered ? color : 'rgba(255,255,255,0.45)';
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl cursor-default select-none"
            style={{
                border: `1px solid ${hovered ? color : 'rgba(255,255,255,0.08)'}`,
                backgroundColor: hovered ? `${color}12` : 'rgba(255,255,255,0.03)',
                transform: inView
                    ? hovered ? 'translateY(-2px) scale(1.03)' : 'translateY(0) scale(1)'
                    : 'translateY(10px)',
                opacity: inView ? 1 : 0,
                transition: `transform 0.2s ease, opacity 0.4s ease ${delay}ms, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease`,
                boxShadow: hovered ? `0 4px 16px ${color}28` : 'none',
            }}
        >
            {isSvg ? (
                <Icon width={17} height={17} style={{ color: iconColor, flexShrink: 0, transition: 'color 0.2s ease' }} />
            ) : (
                <Icon style={{ color: iconColor, fontSize: 17, flexShrink: 0, transition: 'color 0.2s ease' }} />
            )}
            <span style={{ fontSize: '0.78rem', color: hovered ? '#fff' : 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', transition: 'color 0.2s ease', fontFamily: 'Manrope, sans-serif' }}>
                {name}
            </span>
        </div>
    );
}

function CategoryCard({ category, label, index }) {
    const [ref, inView] = useInView(0.1);
    return (
        <div
            ref={ref}
            className="rounded-2xl p-5"
            style={{
                border: `1px solid ${inView ? category.border : 'transparent'}`,
                backgroundColor: inView ? category.accent : 'transparent',
                transform: inView ? 'translateY(0)' : 'translateY(20px)',
                opacity: inView ? 1 : 0,
                transition: `transform 0.5s ease ${index * 80}ms, opacity 0.5s ease ${index * 80}ms, border-color 0.5s ease ${index * 80}ms, background-color 0.5s ease ${index * 80}ms`,
            }}
        >
            <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: category.color }} />
                <span style={{ fontSize: '0.68rem', color: category.color, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
                    {label}
                </span>
            </div>
            <div className="flex flex-wrap gap-2">
                {category.skills.map((skill, i) => (
                    <SkillPill key={i} {...skill} delay={i * 40} inView={inView} isSvg={skill.isSvg} />
                ))}
            </div>
        </div>
    );
}

function CVModal({ onClose, cvFile, labels }) {
    const [numPages, setNumPages]     = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(false);
    const [width, setWidth]           = useState(600);
    const containerRef                = useRef(null);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setWidth(Math.min(containerRef.current.offsetWidth - 32, 700));
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        window.addEventListener('orientationchange', updateWidth);
        return () => {
            window.removeEventListener('resize', updateWidth);
            window.removeEventListener('orientationchange', updateWidth);
        };
    }, []);

    // Lock background scroll while the modal is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return createPortal(
        <div
            className="fixed inset-0 flex items-center justify-center bg-black/75 backdrop-blur-sm"
            onClick={onClose}
            style={{
                zIndex: 2000,
                animation: 'fadeIn 0.2s ease',
                padding: 'clamp(8px, 3vw, 24px)',
                // dvh tracks the real visible area on mobile (browser chrome shrinks/grows)
                height: '100dvh',
            }}
        >
            <style>{`
                @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
                @keyframes slideUp { from { opacity:0; transform:translateY(20px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }
            `}</style>
            <div
                ref={containerRef}
                className="relative flex flex-col bg-[#0a0a0a] border border-white/10 rounded-2xl w-full overflow-hidden"
                style={{
                    maxWidth: 780,
                    height: '100%',
                    maxHeight: 'min(100%, 1000px)',
                    animation: 'slideUp 0.25s ease',
                    boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05)',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0"
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
                >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#B209AA' }} />
                        <span className="truncate" style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '0.95rem' }}>
                            {labels.title}
                        </span>
                        {numPages && (
                            <span style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Manrope, sans-serif', fontSize: '0.75rem' }}>
                                {pageNumber} / {numPages}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={() => window.open(cvFile, '_blank')}
                            className="flex items-center gap-1.5 text-sm font-medium px-3 sm:px-4 py-2 rounded-xl transition-all duration-200"
                            style={{
                                border: '1px solid rgba(178,9,170,0.4)',
                                backgroundColor: 'rgba(178,9,170,0.1)',
                                color: '#B209AA',
                                fontFamily: 'Manrope, sans-serif',
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = 'rgba(178,9,170,0.2)';
                                e.currentTarget.style.borderColor = 'rgba(178,9,170,0.7)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = 'rgba(178,9,170,0.1)';
                                e.currentTarget.style.borderColor = 'rgba(178,9,170,0.4)';
                            }}
                        >
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span className="hidden sm:inline">{labels.download}</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="flex items-center justify-center w-8 h-8 rounded-xl transition-all duration-200"
                            style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                        >
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* PDF */}
                <div className="flex-1 overflow-y-auto flex flex-col items-center py-6 px-4" style={{ minHeight: 0 }}>
                    {error ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-16">
                            <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '3rem' }}>⚠</div>
                            <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif', fontSize: '0.9rem' }}>
                                {labels.error}
                            </p>
                            <button
                                onClick={() => window.open(cvFile, '_blank')}
                                className="flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-xl transition-all duration-200"
                                style={{
                                    border: '1px solid rgba(178,9,170,0.5)',
                                    backgroundColor: 'rgba(178,9,170,0.12)',
                                    color: '#B209AA',
                                    fontFamily: 'Manrope, sans-serif',
                                }}
                            >
                                <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                {labels.downloadInstead}
                            </button>
                        </div>
                    ) : (
                        <>
                            {loading && (
                                <div className="flex flex-col items-center gap-3 py-16">
                                    <div style={{
                                        width: 32, height: 32,
                                        border: '2px solid rgba(178,9,170,0.2)',
                                        borderTop: '2px solid #B209AA',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite',
                                    }} />
                                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                    <p style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Manrope, sans-serif', fontSize: '0.8rem' }}>
                                        {labels.loading}
                                    </p>
                                </div>
                            )}
                            <Document
                                file={cvFile}
                                onLoadSuccess={({ numPages }) => { setNumPages(numPages); setLoading(false); }}
                                onLoadError={() => { setError(true); setLoading(false); }}
                                loading={null}
                            >
                                {Array.from({ length: numPages || 0 }, (_, i) => (
                                    <div key={i + 1} style={{ marginBottom: i + 1 < (numPages || 1) ? 16 : 0 }}>
                                        <Page
                                            pageNumber={i + 1}
                                            width={width}
                                            renderTextLayer={true}
                                            renderAnnotationLayer={true}
                                        />
                                    </div>
                                ))}
                            </Document>
                        </>
                    )}
                </div>

                {/* Pagination */}
                {numPages > 1 && (
                    <div
                        className="flex items-center justify-center gap-4 px-6 py-3 flex-shrink-0"
                        style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                    >
                        <button
                            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                            disabled={pageNumber <= 1}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
                            style={{
                                backgroundColor: pageNumber <= 1 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)',
                                color: pageNumber <= 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)',
                                fontFamily: 'Manrope, sans-serif',
                                cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            {labels.prev}
                        </button>
                        <span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Manrope, sans-serif', fontSize: '0.75rem' }}>
                            {pageNumber} {labels.of} {numPages}
                        </span>
                        <button
                            onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                            disabled={pageNumber >= numPages}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
                            style={{
                                backgroundColor: pageNumber >= numPages ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)',
                                color: pageNumber >= numPages ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)',
                                fontFamily: 'Manrope, sans-serif',
                                cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {labels.next}
                            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

const About = () => {
    const { t, lang } = useT();
    const [showModal, setShowModal] = useState(false);
    const [bioRef, bioInView]       = useInView(0.1);
    const [imgRef, imgInView]       = useInView(0.1);
    const [stackRef, stackInView]   = useInView(0.1);

    return (
        <section className="w-full bg-black py-16 sm:py-24">
            <div className="px-[6%] sm:px-[8%] lg:px-[10%]">

                {/* Header */}
                <div
                    ref={bioRef}
                    className="mb-14 sm:mb-20"
                    style={{
                        opacity: bioInView ? 1 : 0,
                        transform: bioInView ? 'translateY(0)' : 'translateY(20px)',
                        transition: 'opacity 0.6s ease, transform 0.6s ease',
                    }}
                >
                    <p className="text-xs text-zinc-600 uppercase tracking-[0.2em] mb-3">{t.about.sectionLabel}</p>
                    <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                        {t.about.title}
                    </h2>
                    <div
                        className="mt-3 h-px bg-yellow-300"
                        style={{
                            width: bioInView ? '2rem' : '0',
                            transition: 'width 0.6s ease 0.3s',
                        }}
                    />
                </div>

                {/* Bio + foto */}
                <div className="flex flex-col md:flex-row gap-12 md:gap-16 mb-20 md:mb-24 items-center">

                    <div
                        className="flex-1 min-w-0"
                        style={{
                            opacity: bioInView ? 1 : 0,
                            transform: bioInView ? 'translateX(0)' : 'translateX(-24px)',
                            transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
                        }}
                    >
                        <h3
                            className="text-2xl sm:text-3xl font-bold text-white mb-5 leading-snug"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            {t.about.headline1}<br />
                            <span style={{ color: '#B209AA' }}>{t.about.headline2}</span>
                        </h3>

                        <p
                            className="text-sm sm:text-base leading-relaxed mb-8"
                            style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif', maxWidth: 520 }}
                        >
                            {t.about.bio}
                        </p>

                        {/* Stats */}
                        <div className="flex gap-6 sm:gap-10 mb-8">
                            {t.about.stats.map((stat, i) => (
                                <div
                                    key={i}
                                    style={{
                                        opacity: bioInView ? 1 : 0,
                                        transform: bioInView ? 'translateY(0)' : 'translateY(10px)',
                                        transition: `opacity 0.5s ease ${0.3 + i * 0.1}s, transform 0.5s ease ${0.3 + i * 0.1}s`,
                                    }}
                                >
                                    <p className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        {stat.value}
                                    </p>
                                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Manrope, sans-serif' }}>
                                        {stat.label}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* CV button */}
                        <button
                            onClick={() => setShowModal(true)}
                            className="group flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-xl"
                            style={{
                                border: '1px solid rgba(178,9,170,0.4)',
                                backgroundColor: 'rgba(178,9,170,0.08)',
                                color: '#B209AA',
                                fontFamily: 'Manrope, sans-serif',
                                opacity: bioInView ? 1 : 0,
                                transform: bioInView ? 'translateY(0)' : 'translateY(10px)',
                                transition: 'opacity 0.5s ease 0.5s, transform 0.5s ease 0.5s, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = 'rgba(178,9,170,0.18)';
                                e.currentTarget.style.borderColor = 'rgba(178,9,170,0.7)';
                                e.currentTarget.style.boxShadow = '0 4px 20px rgba(178,9,170,0.25)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = 'rgba(178,9,170,0.08)';
                                e.currentTarget.style.borderColor = 'rgba(178,9,170,0.4)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            {t.about.cvButton}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* Foto */}
                    <div
                        ref={imgRef}
                        className="flex justify-start md:justify-end flex-shrink-0"
                        style={{
                            opacity: imgInView ? 1 : 0,
                            transform: imgInView ? 'translateX(0) scale(1)' : 'translateX(24px) scale(0.97)',
                            transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
                        }}
                    >
                        <div className="relative">
                            <div
                                className="absolute inset-0 rounded-full"
                                style={{
                                    background: 'radial-gradient(circle, rgba(178,9,170,0.25) 0%, transparent 70%)',
                                    filter: 'blur(24px)',
                                    transform: 'scale(1.2)',
                                }}
                            />
                            <div
                                className="absolute -inset-1 rounded-full"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(178,9,170,0.5), rgba(82,39,255,0.3), transparent)',
                                    padding: 2,
                                    borderRadius: '5%',
                                }}
                            />
                            <img
                                src={me2}
                                alt="Raúl Flores"
                                className="relative"
                                style={{
                                    width: 'clamp(280px, 30vw, 380px)',
                                    height: 'clamp(280px, 30vw, 380px)',
                                    borderRadius: '5%',
                                    objectFit: 'cover',
                                    border: '2px solid rgba(178,9,170,0.3)',
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Tech stack header */}
                <div
                    ref={stackRef}
                    className="mb-8"
                    style={{
                        opacity: stackInView ? 1 : 0,
                        transform: stackInView ? 'translateY(0)' : 'translateY(16px)',
                        transition: 'opacity 0.5s ease, transform 0.5s ease',
                    }}
                >
                    <p className="text-xs text-zinc-600 uppercase tracking-[0.2em] mb-2">{t.about.techLabel}</p>
                    <p className="text-white font-semibold text-lg sm:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t.about.techTitle}
                    </p>
                    <div
                        className="mt-2 h-px bg-yellow-300"
                        style={{
                            width: stackInView ? '2rem' : '0',
                            transition: 'width 0.6s ease 0.2s',
                        }}
                    />
                </div>

                {/* Grid categorías */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {CATEGORIES_BASE.map((cat, i) => (
                        <CategoryCard
                            key={i}
                            category={cat}
                            label={t.about.categories[cat.key]}
                            index={i}
                        />
                    ))}
                </div>

            </div>

            {showModal && (
                <CVModal
                    onClose={() => setShowModal(false)}
                    cvFile={lang === 'es' ? cvEs : cvEn}
                    labels={t.about.cvModal}
                />
            )}
        </section>
    );
};

export default About;
